/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import Breadcrumb from '@app/components/breadcrumb'
import {HEADER_STYLE_VARIANTS} from '../header/constants'
import useMultiSite from '../../hooks/use-multi-site'

/**
 * PDCBreadcrumb is a React functional component that renders a customizable breadcrumb navigation
 * for the Page Designer pages, supporting both standard and blog-specific navigation structures.
 *
 * The component dynamically generates breadcrumb items based on the current page context,
 * including support for blog pages, home pages, and root pages. It leverages Chakra UI's
 * styling system to apply variant-based styles according to the current page's header style.
 *
 * Props:
 * @param {Object} currentPage - The current page object, containing page data and metadata.
 * @param {Object} homePage - The home page object, used as the root breadcrumb item.
 * @param {string} homeUrl - The URL for the home page link.
 * @param {boolean} isBlog - Indicates if the breadcrumb is being rendered in a blog context.
 * @param {boolean} isHomePage - Indicates if the current page is the home page.
 * @param {boolean} isRootPage - Indicates if the current page is the root page.
 * @param {...Object} props - Additional props to be passed to the Breadcrumb component.
 *
 * The component conditionally renders breadcrumb items for blog and non-blog contexts,
 * and applies appropriate styles to the current and last breadcrumb items. It also
 * integrates with Chakra UI's multi-style configuration for consistent theming.
 */
const PDCBreadcrumb = ({
    currentPage,
    homePage,
    homeUrl,
    blogCategoryPage,
    isBlog,
    isHomePage,
    isArticlePage,
    isRootPage,
    ...props
}) => {
    const headerStyle = currentPage?.data?.headerStyle
    const headerStyleVariant = HEADER_STYLE_VARIANTS[headerStyle] || ''
    const styles = useMultiStyleConfig('Breadcrumb', {variant: headerStyleVariant})
    const {buildUrl} = useMultiSite()

    return (
        <Breadcrumb
            pageData={
                isBlog && isHomePage
                    ? {
                          currentPage: currentPage,
                          homePage: {id: currentPage?.id, name: homePage?.name},
                          homeUrl: homeUrl
                      }
                    : {
                          currentPage: currentPage,
                          homePage: homePage,
                          homeUrl: homeUrl,
                          blogCategoryPage: blogCategoryPage
                      }
            }
            type="content"
            {...props}
        >
            {/* PDCBlogSubmenu component */}
            {isBlog &&
                homePage &&
                (isHomePage ? (
                    <ChakraBreadcrumbItem isCurrentPage>
                        <ChakraBreadcrumbLink {...styles.lastitem}>
                            {homePage?.name}
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                ) : (
                    <ChakraBreadcrumbItem>
                        <ChakraBreadcrumbLink as={RouteLink} to={homeUrl}>
                            {homePage?.name}
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                ))}

            {isBlog && isArticlePage && blogCategoryPage && (
                <ChakraBreadcrumbItem>
                    <ChakraBreadcrumbLink
                        as={RouteLink}
                        to={buildUrl(`/page/${blogCategoryPage?.id}`)}
                    >
                        {blogCategoryPage?.name}
                    </ChakraBreadcrumbLink>
                </ChakraBreadcrumbItem>
            )}

            {isBlog && !isHomePage && (
                <ChakraBreadcrumbItem isCurrentPage>
                    <ChakraBreadcrumbLink {...styles.lastitem}>
                        {currentPage?.name}
                    </ChakraBreadcrumbLink>
                </ChakraBreadcrumbItem>
            )}
            {/* End: PDCBlogSubmenu component */}

            {/* PDCSubmenu component */}
            {isRootPage && (
                <ChakraBreadcrumbItem isCurrentPage={true}>
                    <ChakraBreadcrumbLink {...styles.lastitem}>
                        {currentPage?.name}
                    </ChakraBreadcrumbLink>
                </ChakraBreadcrumbItem>
            )}
        </Breadcrumb>
    )
}

PDCBreadcrumb.propTypes = {
    currentPage: PropTypes.object,
    homePage: PropTypes.object,
    homeUrl: PropTypes.string,
    blogCategoryPage: PropTypes.object,
    isBlog: PropTypes.bool,
    isHomePage: PropTypes.bool,
    isArticlePage: PropTypes.bool,
    isCategoryPage: PropTypes.bool,
    isRootPage: PropTypes.bool
}

export default PDCBreadcrumb
